import { inject, provideAppInitializer } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { LanguageStore } from './language.store';

/**
 * Initializer that brings the `LanguageStore` up on app startup and holds the
 * bootstrap until the resolved language's translation file is in memory.
 *
 * Instantiating the store runs its `onInit` hook, which is the single place
 * that decides the language — saved preference (`actuar/language`) → machine
 * language (`?lang=`, `<html lang>`, `navigator`) → `/Localization/Detect` →
 * `pt-BR` — persists it, applies it to `TranslateService` and configures the
 * DevExtreme localization.
 *
 * The store resolves and applies the language synchronously but loads its
 * translation file over HTTP, so without this wait the first render can flash
 * raw i18n keys (`PROFILEMENU.LANGUAGES.PORTUGUESE`) before the file lands.
 *
 * `/Localization/Detect` is deliberately NOT awaited: it is a late refinement
 * on top of an already usable language, and blocking on it would put a network
 * round-trip in front of every first boot.
 */
export function initializeLanguage(): Promise<unknown> {
  const languageStore = inject(LanguageStore);

  // A failed translation load must never hang the bootstrap — the app still
  // renders, falling back to the keys, which is what happened before the wait.
  return firstValueFrom(languageStore.whenLanguageReady()).catch(
    () => undefined
  );
}

/**
 * Provider for language initialization, shared by every app shell.
 */
export const LANGUAGE_INITIALIZER = provideAppInitializer(initializeLanguage);
